import { motion } from "framer-motion";
import { Compass, Leaf, Database, Users } from "lucide-react";
import { reveal } from "@/components/site/motionPresets";

const PARAGRAPHS = [
  "I spent years running online communities and watching people struggle with tools that didn't fit. OneVidaria started as a way to build simpler versions of those tools.",
  "Instead of patching old systems, I build small tools from scratch — each one solving one clear problem.",
  "Daubaria and Vidaria are in early release, Agos is testing by invitation, SievePulse is used internally, Pocket Peachy is coming soon, Evi is a working prototype, and four more products are in progress.",
];

const VISION = [
  {
    Icon: Compass,
    title: "Ship early",
    detail: "Small releases, tested with real users.",
  },
  {
    Icon: Leaf,
    title: "Simple design",
    detail: "Screens that are easy to understand and use.",
  },
  {
    Icon: Database,
    title: "Checked by people",
    detail: "A person reviews data before it goes out.",
  },
  {
    Icon: Users,
    title: "Built with users",
    detail: "Shaped by growers and players.",
  },
];

export default function Ethos() {
  return (
    <section
      id="ethos"
      data-testid="ethos-section"
      aria-label="About OneVidaria Digital"
      className="relative pt-28 sm:pt-36 pb-12 sm:pb-16"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {/* Vision strip — luxury-portfolio register, flat execution. */}
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line lg:grid-cols-4">
          {VISION.map(({ Icon, title, detail }, i) => (
            <motion.div
              key={title}
              {...reveal({ y: 16, duration: 0.55, delay: i * 0.06 })}
              className="bg-surface-1 p-6"
            >
              <Icon size={20} strokeWidth={1.5} className="text-gold" />
              <p className="font-display mt-4 text-sm font-medium text-white">
                {title}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-white/65">
                {detail}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Sticky-ish heading column */}
          <motion.div
            {...reveal({ y: 24, duration: 0.8, margin: "-100px" })}
            className="lg:col-span-5"
          >
            <h2
              data-testid="ethos-heading"
              className="font-serif-display text-3xl font-medium leading-[1.08] tracking-tight text-cream sm:text-4xl lg:text-5xl"
            >
              Built from years of running online communities
            </h2>

            <div className="mt-10 relative rounded-xl border border-line bg-surface-1 p-6 sm:p-7 overflow-hidden">
              <div aria-hidden className="h-px w-10 bg-gold opacity-70" />
              <div className="relative mt-5">
                <p
                  className="font-serif-display text-lg sm:text-xl leading-snug text-cream"
                  data-testid="ethos-signature"
                >
                  — Enrique Olorvida
                </p>
                <p className="mt-3 text-sm leading-relaxed text-white/70">
                  Founder, OneVidaria Digital
                  <span aria-hidden className="mx-2 text-white/25">
                    ·
                  </span>
                  Las Piñas, Philippines
                </p>
              </div>
            </div>
          </motion.div>

          {/* Story — lede + supporting paragraphs, hairline rhythm. */}
          <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-line">
            <div className="divide-y divide-line">
              {PARAGRAPHS.map((p, i) => (
                <motion.div
                  key={i}
                  {...reveal({
                    y: 20,
                    duration: 0.7,
                    delay: i * 0.08,
                    margin: "-60px",
                  })}
                  data-testid={`ethos-paragraph-${i}`}
                  className={i === 0 ? "pb-10" : "py-10"}
                >
                  <p
                    className={
                      i === 0
                        ? "font-serif-display text-xl sm:text-2xl leading-relaxed text-cream"
                        : "text-base sm:text-lg leading-relaxed text-white/70"
                    }
                  >
                    {p}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
